import { SimulationStore } from 'store/simulation/types'
import { SimulationActionTypes } from 'store/simulation/actions/types'
import { CLEAR_SIMULATION } from 'store/simulation/actions/clearSimulation'
import { HIDE_SIMULATION } from 'store/simulation/actions/hideSimulation'
import { SHOW_SIMULATION } from 'store/simulation/actions/showSimulation'
import { SimulationSteps } from 'features/simulation/types'
import { GO_TO_SIMULATION_STEP, NEXT_SIMULATION_STEP, PREVIOUS_SIMULATION_STEP } from 'store/simulation/actions/switchSimulationStep'
import { changeSimulationFlowBasedOnGivenAnswer, getNextSimulationStep, getPreviousSimulationStep } from 'features/simulation/utils'
import { UPDATE_SIMULATION_INPUT } from 'store/simulation/actions/updateSimulationInput'
import { SIMULATE_PRICE_ERROR, SIMULATE_PRICE_SUCCESS, START_SIMULATION } from 'store/simulation/actions/simulatePrice'
import { SAVE_PARSED_SIMULATION_DATA } from 'store/simulation/actions/saveParsedSimulationData'
import { UPDATE_ALL_SIMULATION_INPUTS } from 'store/simulation/actions/updateAllSimulationInputs'
import { CHANGE_SIMULATION } from 'store/simulation/actions/changeSimulation'
import { TOGGLE_CONTACT_ME } from './actions/toggleContactMe'
import { RESET_STORE } from './actions/resetSimulationStore'
import { UPDATE_FIXED_PRICES } from './actions/updateFixedPrices'

const initialState: SimulationStore = {
  isLoading: false,
  error: false,
  currentStep: SimulationSteps.POSTAL_CODE,
  didSimulation: false,
  visible: false,
  contactMe: false,
  inputs: {
    region: null,
    hasVat: null,
    hasSolarPanels: null,
    counterType: null,
    solarPower: null,
    solarPanels: null,
    needsGas: null,
    knowsUsage: null,
    meterType: null,
    hasExclusiveNightMeter: null,
    buildingType: null,
    residents: null,
    usage: {
      singleRate: null,
      exclNight: null,
      gas: null,
      doubleRate: {
        day: null,
        night: null
      }
    },
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    permission: false,
    priceWithoutPromo: null,
    extraInstalment: null,
    electricityProduct: null,
    gasProduct: null,
    haugazelProducts: {
      electricity: null,
      gas: null
    },
    deliveryPoint: {
      address: {
        postalCode: '',
        townCode: '',
        townName: ''
      }
    },
    usageType: {},
    promoCode: null,
    referralCode: null
  },
  simulationFlow: [
    SimulationSteps.POSTAL_CODE,
    SimulationSteps.BUYER_TYPE,
    SimulationSteps.COUNTER_TYPE,
    SimulationSteps.HAS_SOLAR_PANELS,
    SimulationSteps.SOLAR_POWER,
    SimulationSteps.ENERGY_TYPE,
    SimulationSteps.KNOWS_USAGE,
    SimulationSteps.METER_TYPE,
    SimulationSteps.BUILDING_TYPE,
    SimulationSteps.RESIDENTS,
    SimulationSteps.USAGE,
    SimulationSteps.SUBSCRIBE
  ]
}

export const reducer = (state = initialState, action: SimulationActionTypes): SimulationStore => {
  switch (action.type) {
    case CHANGE_SIMULATION: {
      return {
        ...state,
        visible: true,
        currentStep: SimulationSteps.POSTAL_CODE
      }
    }

    case CLEAR_SIMULATION:
      return initialState

    case GO_TO_SIMULATION_STEP: {
      const { step } = action
      return {
        ...state,
        currentStep: step
      }
    }

    case HIDE_SIMULATION: {
      return {
        ...state,
        visible: false
      }
    }

    case RESET_STORE: {
      return initialState
    }

    case NEXT_SIMULATION_STEP: {
      const { currentStep, simulationFlow } = state
      const nextStep = getNextSimulationStep(simulationFlow, currentStep)

      return {
        ...state,
        currentStep: nextStep
      }
    }

    case PREVIOUS_SIMULATION_STEP: {
      const { currentStep, simulationFlow } = state
      const previousStep = getPreviousSimulationStep(simulationFlow, currentStep)

      return {
        ...state,
        currentStep: previousStep
      }
    }

    case SAVE_PARSED_SIMULATION_DATA: {
      // Fetch data from action
      const { data } = action
      const { meterUsage, product, usageType } = data

      // Update the needed inputs
      const newInputs = {
        ...state.inputs,
        usage: meterUsage,
        product,
        usageType
      }

      return {
        ...state,
        inputs: newInputs
      }
    }

    case SHOW_SIMULATION:
      return {
        ...state,
        visible: true
      }

    case SIMULATE_PRICE_SUCCESS: {
      const { inputs } = state
      const { simulatedPrice } = action

      // Modify usage in inputs
      const newInputs = {
        ...inputs,
        usage: simulatedPrice.usage
      }

      return {
        ...state,
        isLoading: false,
        error: false,
        didSimulation: true,
        inputs: newInputs,
        price: simulatedPrice
      }
    }

    case SIMULATE_PRICE_ERROR:
      return {
        ...state,
        isLoading: false,
        error: true
      }

    case START_SIMULATION:
      return {
        ...state,
        isLoading: true,
        error: false
      }

    case TOGGLE_CONTACT_ME:
      return {
        ...state,
        contactMe: !state.contactMe
      }

    case UPDATE_ALL_SIMULATION_INPUTS: {
      const { inputs } = action

      return {
        ...state,
        inputs
      }
    }

    case UPDATE_FIXED_PRICES: {
      const { electricity, gas } = action

      return {
        ...state,
        fixedPrices: {
          electricity,
          gas
        }
      }
    }

    case UPDATE_SIMULATION_INPUT: {
      const { key, value } = action

      const newInputs = { ...state.inputs }

      // Update the input value
      newInputs[key] = value

      // Check if a Simulation step needs to be removed
      const newSimulationFlow = changeSimulationFlowBasedOnGivenAnswer([...state.simulationFlow], key, newInputs)

      return {
        ...state,
        inputs: newInputs,
        simulationFlow: newSimulationFlow
      }
    }

    default:
      return state
  }
}
